import type { ReactNode } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { DatePicker } from "@/shared/components/calendar/date-picker";
import { FieldTooltip } from "@/shared/components/form/field-tooltip";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";

interface DateFieldProps<TValues extends FieldValues> {
  control: Control<TValues>;
  name: FieldPath<TValues>;
  label: string;
  /// Trigger placeholder when no date is selected.
  placeholder?: string;
  /// `YYYY-MM-DD` lower bound (inclusive).
  min?: string;
  /// `YYYY-MM-DD` upper bound (inclusive).
  max?: string;
  hint?: string;
  /// Plain-language explanation surfaced as an inline info tooltip beside the
  /// label (mirrors SwitchField).
  tooltip?: string;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  /// Passed to the picker: days to mark, each with its hover text.
  marked?: ReadonlyArray<{ date: string; label: string }>;
  /// Passed to the picker: month to open on when empty.
  openTo?: string;
}

/// Date-only field (`YYYY-MM-DD`) wired to react-hook-form.
export function DateField<TValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  min,
  max,
  hint,
  tooltip,
  disabled,
  clearable,
  className,
  marked,
  openTo,
}: DateFieldProps<TValues>): ReactNode {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <div className="flex items-center gap-1.5">
            <FormLabel>{label}</FormLabel>
            {tooltip ? <FieldTooltip term={label} content={tooltip} /> : null}
          </div>
          <FormControl>
            <DatePicker
              value={(field.value as string | null | undefined) ?? null}
              onChange={(next) => field.onChange(next ?? "")}
              placeholder={placeholder}
              min={min}
              max={max}
              disabled={disabled}
              clearable={clearable}
              marked={marked}
              openTo={openTo}
            />
          </FormControl>
          {hint ? <FormDescription>{hint}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
